import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TasksAPI from '../../services/tasksService';

const TARGET_ROLE_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'technician', label: 'Technician' },
  { value: 'front_desk', label: 'Front Desk' }
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
];

const RECURRENCE_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' }
];

const FIXED_PRESETS = [
  { value: 'FREQ=DAILY', label: 'Every day' },
  { value: 'FREQ=WEEKLY', label: 'Every week' },
  { value: 'FREQ=WEEKLY;BYDAY=MO,WE,FR', label: 'Mon / Wed / Fri' },
  { value: 'FREQ=MONTHLY', label: 'Every month' },
  { value: 'FREQ=MONTHLY;BYMONTHDAY=1', label: '1st of each month' },
  { value: 'FREQ=MONTHLY;BYMONTHDAY=15', label: '15th of each month' },
  { value: 'FREQ=YEARLY', label: 'Every year' }
];

class TaskCreateModal extends Component {
  state = {
    title: '',
    nextAction: '',
    description: '',
    priority: 'normal',
    dueDate: null,
    assignedTo: '',
    targetRole: '',
    isRecurring: false,
    recurrenceType: 'after_completion',
    recurrenceInterval: 1,
    recurrenceUnit: 'weeks',
    recurrenceRule: 'FREQ=WEEKLY',
    saving: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    // Reset form when modal opens
    if (this.props.show && !prevProps.show) {
      this.setState({
        title: '',
        nextAction: '',
        description: '',
        priority: 'normal',
        dueDate: null,
        assignedTo: '',
        targetRole: '',
        isRecurring: false,
        recurrenceType: 'after_completion',
        recurrenceInterval: 1,
        recurrenceUnit: 'weeks',
        recurrenceRule: 'FREQ=WEEKLY',
        saving: false,
        error: null
      });
    }
  }

  handleSubmit = async (e) => {
    e.preventDefault();
    const {
      title, nextAction, description, priority, dueDate, assignedTo, targetRole,
      isRecurring, recurrenceType, recurrenceInterval, recurrenceUnit, recurrenceRule
    } = this.state;
    const { installerId, userId, locationId, defaultEntityType, defaultEntityId } = this.props;

    if (!title.trim()) {
      this.setState({ error: 'Title is required.' });
      return;
    }

    if (isRecurring && !dueDate) {
      this.setState({ error: 'Recurring reminders require a due date' });
      return;
    }

    this.setState({ saving: true, error: null });

    try {
      const payload = {
        installer_id: installerId,
        title: title.trim(),
        next_action: nextAction.trim() || null,
        description: description.trim() || null,
        priority,
        due_date: dueDate ? moment(dueDate).format('YYYY-MM-DD') : null,
        assigned_to: assignedTo || null,
        target_role: targetRole || null,
        location_id: locationId || null,
        created_by: userId,
        source: 'manual'
      };

      if (defaultEntityType && defaultEntityId) {
        payload.entity_type = defaultEntityType;
        payload.entity_id = defaultEntityId;
      }

      if (isRecurring) {
        payload.is_recurring = 1;
        payload.recurrence_type = recurrenceType;
        if (recurrenceType === 'after_completion') {
          payload.recurrence_interval = recurrenceInterval;
          payload.recurrence_unit = recurrenceUnit;
        } else {
          payload.recurrence_rule = recurrenceRule;
        }
      }

      await TasksAPI.create(payload);
      this.props.onCreated();
      this.props.onHide();
    } catch (err) {
      console.error('TaskCreateModal: Error creating task', err);
      this.setState({ error: 'Failed to create reminder. Please try again.', saving: false });
    }
  };

  render() {
    const { show, onHide, staffList } = this.props;
    const {
      title, nextAction, description, priority, dueDate, assignedTo, targetRole,
      isRecurring, recurrenceType, recurrenceInterval, recurrenceUnit, recurrenceRule,
      saving, error
    } = this.state;

    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
            <i className="fa-sharp fa-regular fa-bell" style={{ color: '#8B5CF6', marginRight: 8 }} />
            New Reminder
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={this.handleSubmit}>
          <Modal.Body>
            {error && (
              <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Title <span style={{ color: '#DC2626' }}>*</span></Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => this.setState({ title: e.target.value })}
                placeholder="Short action phrase, e.g. Call Tim Jones"
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={nextAction}
                onChange={(e) => this.setState({ nextAction: e.target.value })}
                placeholder="What to do — phone number, amount, details..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Context</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => this.setState({ description: e.target.value })}
                placeholder="Background notes (optional)"
              />
            </Form.Group>

            <div className="row">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Control
                    as="select"
                    value={priority}
                    onChange={(e) => this.setState({ priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <DatePicker
                    selected={dueDate}
                    onChange={(date) => this.setState({ dueDate: date })}
                    dateFormat="MMM d, yyyy"
                    className="form-control"
                    placeholderText="Select due date..."
                    isClearable
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Who is this for?</Form.Label>
                  <Form.Control
                    as="select"
                    value={targetRole}
                    onChange={(e) => this.setState({ targetRole: e.target.value, assignedTo: '' })}
                  >
                    {TARGET_ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Assign To</Form.Label>
                  <Form.Control
                    as="select"
                    value={assignedTo}
                    onChange={(e) => this.setState({ assignedTo: e.target.value })}
                  >
                    <option value="">All</option>
                    {(staffList || [])
                      .filter(s => {
                        if (!targetRole) return true;
                        const r = (s.role || '').toLowerCase();
                        if (targetRole === 'front_desk') return r !== 'technician' && r !== 'owner';
                        return r === targetRole;
                      })
                      .map(s => (
                        <option key={s.User_id || s.user_id} value={s.User_id || s.user_id}>
                          {s.user_name || s.name || `${s.First_name || ''} ${s.Last_name || ''}`.trim()}
                        </option>
                      ))}
                  </Form.Control>
                </Form.Group>
              </div>
            </div>

            {/* Recurrence */}
            <div style={{
              borderTop: '1px solid #dee2e6',
              paddingTop: 12,
              marginTop: 4
            }}>
              <Form.Check
                type="switch"
                id="recurring-toggle"
                label={<span style={{ fontWeight: 500 }}>Repeat</span>}
                checked={isRecurring}
                onChange={(e) => this.setState({ isRecurring: e.target.checked })}
              />

              {isRecurring && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginTop: 8
                }}>
                  <div className="row mb-2">
                    <div className="col">
                      <Form.Control
                        as="select"
                        value={recurrenceType}
                        onChange={(e) => this.setState({ recurrenceType: e.target.value })}
                        style={{ fontSize: '0.9rem' }}
                      >
                        <option value="after_completion">After completion</option>
                        <option value="fixed">Fixed schedule</option>
                      </Form.Control>
                    </div>
                  </div>

                  {recurrenceType === 'after_completion' ? (
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <span style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>Every</span>
                      <Form.Control
                        type="number"
                        min={1}
                        max={365}
                        value={recurrenceInterval}
                        onChange={(e) => this.setState({ recurrenceInterval: Math.max(1, parseInt(e.target.value) || 1) })}
                        style={{ width: 70, fontSize: '0.9rem' }}
                      />
                      <Form.Control
                        as="select"
                        value={recurrenceUnit}
                        onChange={(e) => this.setState({ recurrenceUnit: e.target.value })}
                        style={{ width: 110, fontSize: '0.9rem' }}
                      >
                        {RECURRENCE_UNITS.map(u => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </Form.Control>
                    </div>
                  ) : (
                    <Form.Control
                      as="select"
                      value={recurrenceRule}
                      onChange={(e) => this.setState({ recurrenceRule: e.target.value })}
                      style={{ fontSize: '0.9rem' }}
                    >
                      {FIXED_PRESETS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </Form.Control>
                  )}

                  <small className="text-muted d-block mt-2" style={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                    {recurrenceType === 'after_completion'
                      ? 'A new reminder will be created each time this one is completed.'
                      : 'A new reminder will be created on each scheduled date.'}
                  </small>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
              disabled={saving || !title.trim()}
            >
              {saving ? (
                <><i className="fa-sharp fa-solid fa-spinner fa-spin" /> Creating...</>
              ) : (
                <><i className="fa-sharp fa-regular fa-plus" /> Create Reminder</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}

export default TaskCreateModal;
